<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import CampaignCreatePanel from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignCreatePanel.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const props = defineProps({
  // The campaign to edit; null opens the dialog on a blank new campaign.
  campaign: {
    type: Object,
    default: null,
  },
  // The gallery template this campaign starts from; null for a blank one.
  starterTemplate: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const isEditing = computed(() => Boolean(props.campaign));

const title = computed(() =>
  isEditing.value
    ? t('CAMPAIGN.WHATSAPP.EDIT.TITLE')
    : t('CAMPAIGN.WHATSAPP.CREATE.TITLE')
);

const saveCampaign = async campaignDetails => {
  if (isEditing.value) {
    await store.dispatch('campaigns/update', {
      id: props.campaign.id,
      ...campaignDetails,
    });
    useAlert(t('CAMPAIGN.WHATSAPP.EDIT.API.SUCCESS_MESSAGE'));
    return;
  }

  await store.dispatch('campaigns/create', campaignDetails);
  useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
    type: CAMPAIGN_TYPES.ONE_OFF,
  });
  useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
};

const handleSubmit = async campaignDetails => {
  try {
    await saveCampaign(campaignDetails);
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleClose = () => emit('close');
</script>

<template>
  <CampaignCreatePanel :title="title" @close="handleClose">
    <WhatsAppCampaignForm
      :campaign="campaign"
      :starter-template="starterTemplate"
      @submit="handleSubmit"
      @cancel="handleClose"
    />
  </CampaignCreatePanel>
</template>
